import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChartBarIcon,
  PlusIcon,
  ArrowPathIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import {
  useMonitors,
  useConnectionAlerts,
} from '../../hooks/useMonitors';
import monitorsService from '../../services/monitorsService';
import MonitorCard from './components/MonitorCard';

// Unified monitors page — replaces both AnomalyDashboardPage and
// CustomMetricsPage. Two tabs split the card grid by evaluation_mode:
//
//   - "ML / Statistical" → evaluation_mode='statistical'
//   - "Threshold"        → evaluation_mode='threshold'
//
// The split-by-tab UX (rather than a single grid with a filter
// dropdown) matches how prospects think about the two paths:
// "automated detection learning from history" vs "I told you the line
// is here, alert me when we cross it". Same source types and segments
// work in either tab — the create form (Step 13) picks evaluation
// mode as a separate step from source.
//
// URL: /monitors/:connectionSlug
//      /monitors (no slug)  → redirects to the active connection
//
// Edit / create routes are handled by the form component (Step 13);
// this page exposes `onEdit` and "+ New Monitor" buttons that navigate
// to those routes.

const TABS = [
  {
    id: 'statistical',
    label: 'ML / Statistical',
    icon: BeakerIcon,
    description:
      "Z-score, IQR, or moving-average detection learned from a monitor's history.",
  },
  {
    id: 'threshold',
    label: 'Threshold',
    icon: AdjustmentsHorizontalIcon,
    description:
      'Manual warning and critical thresholds. Alert fires when the value crosses the line you set.',
  },
];

const MonitorsPage = () => {
  const { connectionSlug } = useParams();
  const navigate = useNavigate();
  const { activeConnection, getConnectionBySlug, loading: connectionsLoading } =
    useConnection();
  const { updateBreadcrumbs } = useUI();

  const [activeTab, setActiveTab] = useState('statistical');

  // Resolve connection from URL slug; fall back to the user's active
  // connection if no slug present (then redirect so the URL is canonical).
  const connection = connectionSlug
    ? getConnectionBySlug(connectionSlug)
    : activeConnection;
  const connectionId = connection?.id;

  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Dashboard', href: '/dashboard' },
      { name: 'Monitors' },
    ]);
  }, [updateBreadcrumbs]);

  // If we landed on bare /monitors and have an active connection, push
  // the slugged URL so deep-links work and the back button is sensible.
  useEffect(() => {
    if (!connectionSlug && !connectionsLoading && activeConnection?.slug) {
      navigate(`/monitors/${activeConnection.slug}`, { replace: true });
    }
  }, [connectionSlug, connectionsLoading, activeConnection, navigate]);

  // Load monitors + recent alerts (alerts feed the top-of-page summary).
  const {
    data: monitors = [],
    isLoading: monitorsLoading,
    error: monitorsError,
    refetch: refetchMonitors,
  } = useMonitors(connectionId, {});

  const { data: openAlerts = [] } = useConnectionAlerts(connectionId, {
    status: 'open',
    limit: 50,
  });

  // Split monitors by evaluation mode for the two tabs. Single source
  // of truth for both the tab header counts and the grid contents.
  const monitorsByMode = useMemo(() => {
    const groups = { statistical: [], threshold: [] };
    for (const m of monitors) {
      if (m.evaluation_mode === 'statistical') groups.statistical.push(m);
      else if (m.evaluation_mode === 'threshold') groups.threshold.push(m);
    }
    return groups;
  }, [monitors]);

  const visibleMonitors = monitorsByMode[activeTab] || [];

  const handleNew = () => {
    if (!connectionSlug) return;
    navigate(`/monitors/${connectionSlug}/new`);
  };

  const handleEdit = (monitor) => {
    if (!connectionSlug) return;
    navigate(`/monitors/${connectionSlug}/${monitor.id}`);
  };

  // ------------------------------------------------------------------
  // Empty / loading / error states
  // ------------------------------------------------------------------
  if (connectionsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center text-gray-500">
          <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
          Loading connection...
        </div>
      </div>
    );
  }

  if (!connection) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-sm text-gray-500">
            No connection selected. Pick a connection to see its monitors.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
            <ChartBarIcon className="h-8 w-8 text-indigo-600 mr-3" />
            Monitors
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Detect anomalies and enforce thresholds on {connection.name}.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => refetchMonitors()}
            disabled={monitorsLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-2 ${monitorsLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
          <button
            onClick={handleNew}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Monitor
          </button>
        </div>
      </div>

      {/* Open alerts summary — single banner above the tabs, drives
          user attention to monitors that need action without making
          them context-switch to a separate alerts page first. */}
      {openAlerts.length > 0 && (
        <div className="mb-6 rounded-md bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-start">
            <BellAlertIcon className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                {openAlerts.length} open alert{openAlerts.length === 1 ? '' : 's'}
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Showing the latest{' '}
                {Math.min(openAlerts.length, 5)} below — click a monitor card
                to drill in.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {openAlerts.slice(0, 5).map((alert) => (
                  <span
                    key={alert.id}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${monitorsService.getSeverityBadge(
                      alert.severity
                    )}`}
                  >
                    {alert.severity}: {monitorsService.alertTypeLabel(alert.alert_type)}
                    {alert.segment_value ? ` (${alert.segment_value})` : ''}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load error */}
      {monitorsError && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {monitorsError.message || 'Failed to load monitors'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const count = monitorsByMode[tab.id]?.length ?? 0;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  isActive
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
                <span
                  className={`ml-2 inline-block py-0.5 px-2 rounded-full text-xs ${
                    isActive
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
        <p className="text-xs text-gray-500 mt-2 mb-3">
          {TABS.find((t) => t.id === activeTab)?.description}
        </p>
      </div>

      {/* Card grid */}
      {monitorsLoading ? (
        <div className="flex items-center justify-center py-16">
          <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      ) : visibleMonitors.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-medium text-gray-900">
            No {TABS.find((t) => t.id === activeTab)?.label.toLowerCase()} monitors
            yet
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            {TABS.find((t) => t.id === activeTab)?.description}
          </p>
          <button
            onClick={handleNew}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Your First Monitor
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleMonitors.map((monitor) => (
            <MonitorCard
              key={monitor.id}
              monitor={monitor}
              connectionId={connectionId}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MonitorsPage;
